// libs
import React, { memo } from 'react';
import { storeCoachmarkInteractionComplete, storeCoachmarkVisible } from './helpers';

// hooks
import { useKeyboardClick } from 'shared/hooks/useKeyboardClick';

// styles
import {
  Container,
  DescriptionContainer,
  CTAContainer,
  Pointer,
  TopContainer,
  Icon,
  Title,
  Description,
  TextContainer,
  StepContainer,
  Footer,
  Header,
  CircleXDuoToneIcon,
} from './styles';
import { COLORS } from 'shared/styles/base';

// defs
interface CoachmarkProps {
  title: string;
  description: string;
  imageURL: string;
  cta: string;
  onInteraction?: VoidFunction;
  onVisible?: VoidFunction;
  onDismiss?: VoidFunction;
  stepText?: string;
  pointerClassName?: string;
  imageClassName?: string;
  className?: string;
  id: string;
  iconWidth?: string | number;
  iconHeight?: string | number;
}

const CIRCLEXDUOTONE_ICON_SIZE = '24px';
const IMAGE_DIMENSION = '40px';

const ARIA_LABELS = {
  DISMISS_COACHMARK: 'Dismiss Coachmark',
  NEXT: 'Next',
};

const Coachmark = ({
  title,
  description,
  imageURL,
  cta,
  onInteraction,
  onVisible,
  onDismiss,
  stepText,
  iconWidth = IMAGE_DIMENSION,
  iconHeight = IMAGE_DIMENSION,
  pointerClassName = '',
  className,
  id,
  imageClassName = '',
}: CoachmarkProps) => {
  const onCoachmarkVisible = () => {
    storeCoachmarkVisible(id);
    onVisible && onVisible();
  };

  const onCoachmarkInteraction: React.MouseEventHandler = (e) => {
    e.stopPropagation();
    storeCoachmarkInteractionComplete(id);
    onInteraction && onInteraction();
  };

  const onCoachmarkDismiss: React.MouseEventHandler = (e) => {
    e.stopPropagation();
    storeCoachmarkInteractionComplete(id);
    onDismiss && onDismiss();
  };

  const dismissKeyboardProps = useKeyboardClick(onCoachmarkDismiss);
  const nextKeyboardProps = useKeyboardClick(onCoachmarkInteraction);

  return (
    <TopContainer className={className} onAnimationEnd={onCoachmarkVisible}>
      <Pointer className={pointerClassName} />
      <Container>
        <DescriptionContainer>
          <Icon>
            <img
              src={imageURL}
              height={iconHeight}
              width={iconWidth}
              alt=""
              className={imageClassName}
            />
          </Icon>
          <TextContainer>
            <Header>
              <Title> {title} </Title>
              {/*  eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
              <div {...dismissKeyboardProps} aria-label={ARIA_LABELS.DISMISS_COACHMARK}>
                <CircleXDuoToneIcon
                  size={CIRCLEXDUOTONE_ICON_SIZE}
                  color={COLORS.WHITE_100}
                  circleFill={COLORS.BLACK_64}
                />
              </div>
            </Header>
            <Description>{description}</Description>
          </TextContainer>
        </DescriptionContainer>
        <Footer>
          <StepContainer>{stepText}</StepContainer>
          <CTAContainer {...nextKeyboardProps} aria-label={ARIA_LABELS.NEXT}>{cta}</CTAContainer>
        </Footer>
      </Container>
    </TopContainer>
  );
};

export default memo(Coachmark);
